import { useState } from "react"

import { useTranslation } from "react-i18next"

import { Check, Columns3, FileText, LayoutTemplate, Play } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { AddColumnsDialog } from "./add-columns-dialog"
import { ApplyTemplateDialog } from "./apply-template-dialog"
import { FilePickerDialog } from "../../chat/components/file-picker-dialog"
import { useDirectFileUpload } from "../hooks/use-direct-file-upload"

type AIParserEmptyStateProps = {
  hasFiles: boolean
  hasColumns: boolean
  onRunParser?: () => void
  isRunDisabled?: boolean
}

export function AIParserEmptyState({
  hasFiles,
  hasColumns,
  onRunParser,
  isRunDisabled,
}: AIParserEmptyStateProps) {
  const { t } = useTranslation()
  const [isAddColumnsDialogOpen, setIsAddColumnsDialogOpen] = useState(false)
  const [isApplyTemplateDialogOpen, setIsApplyTemplateDialogOpen] = useState(false)

  const {
    isFilePickerOpen,
    setIsFilePickerOpen,
    openFilePicker,
    handleLocalFilesSelected,
    handleGraphFileSelect,
    handleDocumentsSelected,
  } = useDirectFileUpload()

  const ctaButtonInteractionClassName =
    "card-interactive shadow-sm active:scale-[0.98] active:shadow-sm"
  const ctaPrimaryButtonInteractionClassName = cn(
    ctaButtonInteractionClassName,
    "hover:bg-primary/85"
  )

  const getCurrentStep = () => {
    if (!hasFiles) return 1
    if (!hasColumns) return 2
    return 3
  }

  const currentStep = getCurrentStep()

  return (
    <div className="flex h-full flex-col items-center justify-center p-8">
      <div className="animate-in fade-in-0 slide-in-from-bottom-4 flex max-w-2xl flex-col items-center text-center duration-500">
        <h1 className="text-foreground mb-4 text-4xl font-semibold tracking-tight">AI Parser</h1>

        <h2 className="text-foreground mb-2 text-lg font-medium">
          {t("pages.ai_parser.empty_state.title")}
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md">
          {t("pages.ai_parser.empty_state.description")}
        </p>

        <div className="mb-8 flex w-full max-w-lg items-center justify-between gap-2">
          <StepIndicator
            description={t("pages.ai_parser.empty_state.step1_desc")}
            isActive={currentStep === 1}
            isCompleted={hasFiles}
            stepNumber={1}
            title={t("pages.ai_parser.empty_state.step1")}
          />
          <div
            className={cn(
              "h-0.5 flex-1 rounded-full transition-colors duration-500",
              hasFiles ? "bg-primary" : "bg-border"
            )}
          />
          <StepIndicator
            description={t("pages.ai_parser.empty_state.step2_desc")}
            isActive={currentStep === 2}
            isCompleted={hasColumns}
            stepNumber={2}
            title={t("pages.ai_parser.empty_state.step2")}
          />
          <div
            className={cn(
              "h-0.5 flex-1 rounded-full transition-colors duration-500",
              hasColumns ? "bg-primary" : "bg-border"
            )}
          />
          <StepIndicator
            description={t("pages.ai_parser.empty_state.step3_desc")}
            isActive={currentStep === 3}
            isCompleted={false}
            stepNumber={3}
            title={t("pages.ai_parser.empty_state.step3")}
          />
        </div>

        <div className="flex flex-col gap-3">
          {currentStep === 1 && (
            <>
              <Button
                className={cn("gap-2", ctaPrimaryButtonInteractionClassName)}
                size="lg"
                onClick={openFilePicker}
              >
                <FileText className="h-5 w-5" />
                {t("pages.ai_parser.empty_state.add_documents")}
              </Button>
            </>
          )}

          {currentStep === 2 && (
            <>
              <Button
                className={cn("gap-2", ctaPrimaryButtonInteractionClassName)}
                size="lg"
                onClick={() => setIsAddColumnsDialogOpen(true)}
              >
                <Columns3 className="h-5 w-5" />
                {t("pages.ai_parser.empty_state.define_columns")}
              </Button>
              <Button
                className={cn("gap-2", ctaButtonInteractionClassName)}
                size="lg"
                variant="outline"
                onClick={() => setIsApplyTemplateDialogOpen(true)}
              >
                <LayoutTemplate className="h-5 w-5" />
                {t("pages.ai_parser.empty_state.use_template")}
              </Button>
            </>
          )}

          {currentStep === 3 && (
            <Button
              className={cn("gap-2", ctaPrimaryButtonInteractionClassName)}
              disabled={isRunDisabled}
              size="lg"
              onClick={onRunParser}
            >
              <Play className="h-5 w-5" />
              {t("pages.ai_parser.empty_state.run_extraction")}
            </Button>
          )}
        </div>

        <p className="text-muted-foreground mt-6 text-xs">
          {t("pages.ai_parser.empty_state.drag_drop_hint")}
        </p>
      </div>

      <FilePickerDialog
        open={isFilePickerOpen}
        source="recent"
        onDocumentsSelected={handleDocumentsSelected}
        onLocalFilesSelected={handleLocalFilesSelected}
        onOpenChange={setIsFilePickerOpen}
        onSelect={handleGraphFileSelect}
      />
      <AddColumnsDialog
        hasFiles={hasFiles}
        isOpen={isAddColumnsDialogOpen}
        onOpenChange={setIsAddColumnsDialogOpen}
      />
      <ApplyTemplateDialog
        isOpen={isApplyTemplateDialogOpen}
        onOpenChange={setIsApplyTemplateDialogOpen}
      />
    </div>
  )
}

type StepIndicatorProps = {
  stepNumber: number
  title: string
  description: string
  isActive: boolean
  isCompleted: boolean
}

function StepIndicator({
  stepNumber,
  title,
  description,
  isActive,
  isCompleted,
}: StepIndicatorProps) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium transition-[color,background-color,box-shadow,scale] duration-300",
          isCompleted && "border-foreground bg-primary text-primary-foreground scale-100 border",
          isActive &&
            !isCompleted &&
            "bg-primary/20 text-primary ring-primary ring-offset-background ring-2 ring-offset-2",
          !isActive && !isCompleted && "bg-muted text-muted-foreground"
        )}
      >
        {isCompleted ? <Check className="check-animated h-5 w-5" /> : stepNumber}
      </div>
      <span
        className={cn(
          "text-sm font-medium transition-colors duration-200",
          isActive || isCompleted ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {title}
      </span>
      <span className="text-muted-foreground text-xs">{description}</span>
    </div>
  )
}
